import { useEffect, useRef, useState } from "react";
import type { PatchOperation } from "@enrein/shared-contracts";
import {
  CLIENT_SAFE_EDIT_ACCEPTED_MESSAGE,
  CLIENT_SAFE_EDIT_ACTIVE_MESSAGE,
  CLIENT_SAFE_EDIT_CONTRADICTION_BLOCKED_MESSAGE,
  CLIENT_SAFE_EDIT_FAILED_MESSAGE,
  CLIENT_SAFE_EDIT_QA_BLOCKED_MESSAGE,
  CLIENT_SAFE_EDIT_REJECTED_MESSAGE,
  toClientApiErrorMessage,
} from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";

const WEEKDAY_HOURS = [
  { dayOfWeek: 1, label: "Monday" },
  { dayOfWeek: 2, label: "Tuesday" },
  { dayOfWeek: 3, label: "Wednesday" },
  { dayOfWeek: 4, label: "Thursday" },
  { dayOfWeek: 5, label: "Friday" },
] as const;

type WeekdayHoursRow = {
  readonly dayOfWeek: number;
  readonly open: string;
  readonly close: string;
};

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function buildHoursPayload(rows: readonly WeekdayHoursRow[]) {
  return rows
    .filter((row) => row.open.trim().length > 0)
    .map((row) => ({
      dayOfWeek: row.dayOfWeek,
      open: row.open.trim(),
      ...(row.close.trim().length > 0 ? { close: row.close.trim() } : {}),
    }));
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function SafeEditPanel({
  api,
  clientLinkId,
  token,
  onStatus,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
  readonly onStatus: (message: string | null) => void;
}) {
  const [primaryPhone, setPrimaryPhone] = useState("");
  const [primaryEmail, setPrimaryEmail] = useState("");
  const [hoursRows, setHoursRows] = useState<WeekdayHoursRow[]>(
    WEEKDAY_HOURS.map((day) => ({ dayOfWeek: day.dayOfWeek, open: "", close: "" })),
  );
  const [submitting, setSubmitting] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  const trimmedPhone = primaryPhone.trim();
  const trimmedEmail = primaryEmail.trim();
  const hoursPayload = buildHoursPayload(hoursRows);
  const hasPhone = trimmedPhone.length >= 7;
  const hasEmail = trimmedEmail.length > 0 && isValidEmail(trimmedEmail);
  const hasHours = hoursPayload.length > 0;
  const canSubmit = (hasPhone || hasEmail || hasHours) && !submitting;

  function updateHoursRow(dayOfWeek: number, field: "open" | "close", value: string) {
    setHoursRows((rows) => rows.map((row) => (row.dayOfWeek === dayOfWeek ? { ...row, [field]: value } : row)));
  }

  async function pollOutcome(attemptId: string) {
    for (let attempt = 0; attempt < 30; attempt += 1) {
      if (cancelledRef.current) return null;
      await sleep(500);
      const polled = await api.getSafeEditOutcome(clientLinkId, token, attemptId);
      if (cancelledRef.current) return null;
      if (polled.compilationPhase === "READY" ||
          polled.compilationPhase === "FAILED" ||
          polled.compilationPhase === "QA_BLOCKED" ||
          polled.compilationPhase === "CONTRADICTION_BLOCKED" ||
          polled.compilationPhase === "ACTIVATION_REJECTED" ||
          polled.compilationPhase === "SUPERSEDED") {
        return polled;
      }
    }
    if (cancelledRef.current) return null;
    return api.getSafeEditOutcome(clientLinkId, token, attemptId);
  }

  async function submit() {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    onStatus(null);
    try {
      const operations: PatchOperation[] = [];
      if (hasPhone) {
        operations.push({ op: "set", path: "/business/primaryPhone", value: trimmedPhone });
      }
      if (hasEmail) {
        operations.push({ op: "set", path: "/business/primaryEmail", value: trimmedEmail });
      }
      if (hasHours) {
        operations.push({ op: "set", path: "/operations/hours", value: hoursPayload });
      }
      const patchId = `patch_${crypto.randomUUID()}`;
      const outcome = await api.submitSafeEditPatch(clientLinkId, token, {
        schemaVersion: "client-safe-edit-patch.v1.0",
        patchId,
        operations,
      });
      const needsPoll =
        outcome.accepted &&
        outcome.compilationPhase !== "READY" &&
        outcome.compilationPhase !== "FAILED" &&
        outcome.compilationPhase !== "QA_BLOCKED" &&
        outcome.compilationPhase !== "CONTRADICTION_BLOCKED" &&
        outcome.compilationPhase !== "ACTIVATION_REJECTED" &&
        outcome.compilationPhase !== "SUPERSEDED" &&
        outcome.compilationPhase !== "NONE";
      const polled = needsPoll ? await pollOutcome(outcome.attemptId) : null;
      if (cancelledRef.current) return;
      const finalOutcome = polled ?? outcome;
      if (
        needsPoll &&
        polled &&
        polled.compilationPhase !== "READY" &&
        polled.compilationPhase !== "FAILED" &&
        polled.compilationPhase !== "QA_BLOCKED" &&
        polled.compilationPhase !== "CONTRADICTION_BLOCKED" &&
        polled.compilationPhase !== "ACTIVATION_REJECTED" &&
        polled.compilationPhase !== "SUPERSEDED"
      ) {
        onStatus("Your safe edit is still processing. It is not live yet. Check back shortly or contact your agency.");
        return;
      }
      if (!finalOutcome.accepted) {
        onStatus(CLIENT_SAFE_EDIT_REJECTED_MESSAGE);
        return;
      }
      switch (finalOutcome.compilationPhase) {
        case "READY":
          onStatus(finalOutcome.clientMessage ?? CLIENT_SAFE_EDIT_ACTIVE_MESSAGE);
          break;
        case "QA_BLOCKED":
          onStatus(finalOutcome.clientMessage ?? CLIENT_SAFE_EDIT_QA_BLOCKED_MESSAGE);
          break;
        case "CONTRADICTION_BLOCKED":
          onStatus(finalOutcome.clientMessage ?? CLIENT_SAFE_EDIT_CONTRADICTION_BLOCKED_MESSAGE);
          break;
        case "ACTIVATION_REJECTED":
        case "FAILED":
        case "SUPERSEDED":
          onStatus(finalOutcome.clientMessage ?? CLIENT_SAFE_EDIT_FAILED_MESSAGE);
          break;
        default:
          onStatus(finalOutcome.clientMessage ?? CLIENT_SAFE_EDIT_ACCEPTED_MESSAGE);
      }
    } catch (err) {
      if (cancelledRef.current) return;
      onStatus(
        err instanceof Error ? toClientApiErrorMessage(err.message) : toClientApiErrorMessage(String(err)),
      );
    } finally {
      if (!cancelledRef.current) {
        setSubmitting(false);
      }
    }
  }

  return (
    <section className="panel value-card" aria-label="Safe edit">
      <p className="value-card-eyebrow">Limited update</p>
      <h2 className="value-card-headline">Safe edit</h2>
      <p>
        Submit limited updates to your phone, email, or weekday hours. Your website will be updated after your
        agency processes an accepted change.
      </p>
      <label className="field-label" htmlFor="safe-edit-phone">
        Primary phone
      </label>
      <input
        id="safe-edit-phone"
        value={primaryPhone}
        onChange={(event) => setPrimaryPhone(event.target.value)}
        placeholder="(555) 555-5555"
        autoComplete="tel"
      />
      <label className="field-label" htmlFor="safe-edit-email">
        Primary email
      </label>
      <input
        id="safe-edit-email"
        type="email"
        value={primaryEmail}
        onChange={(event) => setPrimaryEmail(event.target.value)}
        placeholder="hello@example.com"
        autoComplete="email"
      />
      <fieldset className="safe-edit-hours">
        <legend className="field-label">Weekday hours (Mon–Fri)</legend>
        <ul className="safe-edit-hours-list">
          {WEEKDAY_HOURS.map((day) => {
            const row = hoursRows.find((entry) => entry.dayOfWeek === day.dayOfWeek);
            if (!row) return null;
            return (
              <li key={day.dayOfWeek} className="safe-edit-hours-row">
                <span className="safe-edit-hours-day">{day.label}</span>
                <label className="sr-only" htmlFor={`safe-edit-open-${day.dayOfWeek}`}>
                  {day.label} open
                </label>
                <input
                  id={`safe-edit-open-${day.dayOfWeek}`}
                  type="time"
                  value={row.open}
                  onChange={(event) => updateHoursRow(day.dayOfWeek, "open", event.target.value)}
                />
                <span aria-hidden="true">to</span>
                <label className="sr-only" htmlFor={`safe-edit-close-${day.dayOfWeek}`}>
                  {day.label} close
                </label>
                <input
                  id={`safe-edit-close-${day.dayOfWeek}`}
                  type="time"
                  value={row.close}
                  onChange={(event) => updateHoursRow(day.dayOfWeek, "close", event.target.value)}
                />
              </li>
            );
          })}
        </ul>
      </fieldset>
      <div className="action-row">
        <button type="button" className="btn-primary" onClick={() => void submit()} disabled={!canSubmit}>
          {submitting ? "Submitting…" : "Submit safe edit"}
        </button>
      </div>
    </section>
  );
}
